// گزارش کلی درخواست کالا / خدمات
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, toFa, splitLines, runReport, MsgBox, AskBox, Field, DateField, Grid, Modal,
} from './RequestsShared';

const FORM = 'FRMGOODSREQUEST';
const MODES = ['—', 'سرجمع گزارش', 'سرجمع میانی', 'سطر گزارش'];   // 1، 2، 3
const NO_LIST = new Set(['StockSlip']);

export default function GoodsRequestAllReports({ user, onBack, year }) {
  const fb = useFormButtons(FORM, ['BTNPROPERTYALLREPORTS']);
  const [init, setInit] = useState(null);
  const [modes, setModes] = useState({ Goods: 3 });
  const [opts, setOpts] = useState({});          // item → rows با sel
  const [active, setActive] = useState('Goods');
  const [dates, setDates] = useState({ from_date: '', to_date: '', from_factor_date: '', to_factor_date: '' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveDlg, setSaveDlg] = useState(null);
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(null);
  const say = m => setMsg(Array.isArray(m) ? m : splitLines(m));

  const loadSaved = useCallback(async () => {
    const j = await API('/all-reports/saved/');
    if (j.ok) setSaved(j.data || []);
  }, []);

  useEffect(() => {
    if (!fb.open) return;
    API(`/all-reports/init/?year=${year || ''}`).then(j => {
      if (!j.ok) { say(j.msg); return; }
      setInit(j);
      setDates(d => ({ ...d, from_date: j.from_date, to_date: j.to_date }));
    });
    loadSaved();
  }, [fb.open, year, loadSaved]);

  const loadOpts = useCallback(async (item, excluded) => {
    if (NO_LIST.has(item)) return;
    const j = await API(`/all-reports/options/${item}/?year=${year || ''}`);
    if (!j.ok) return;
    const ex = new Set((excluded || []).map(String));
    setOpts(o => ({ ...o, [item]: (j.data || []).map(r => ({ ...r, sel: excluded ? (ex.has(String(r.id)) ? 0 : 1) : r.sel })) }));
  }, [year]);

  useEffect(() => { if (init && active && !opts[active]) loadOpts(active); }, [init, active, opts, loadOpts]);

  const toggle = (item, id) => setOpts(o => ({ ...o, [item]: o[item].map(r => (r.id === id ? { ...r, sel: r.sel ? 0 : 1 } : r)) }));
  const setAll = (item, v) => setOpts(o => ({ ...o, [item]: (o[item] || []).map(r => ({ ...r, sel: v === 'inv' ? (r.sel ? 0 : 1) : v })) }));

  const state = () => {
    const selections = {};
    Object.entries(opts).forEach(([k, rows]) => {
      selections[k] = { sel: rows.filter(r => r.sel).map(r => r.id), unsel: rows.filter(r => !r.sel).map(r => r.id) };
    });
    return { ...dates, year, modes, selections };
  };

  const prepare = async () => {
    setBusy(true);
    const j = await API('/all-reports/prepare/', { method: 'POST', body: JSON.stringify(state()) });
    setBusy(false);
    if (!j.ok) { say(j.msg); return; }
    setResult({ cols: j.columns || [], rows: (j.data || []).map((r, i) => ({ ...r, id: i })) });
  };
  const print = () => runReport('tadarokat.goods_request_all_reports', { state: JSON.stringify(state()) }, say);

  // ── انتخاب‌های ذخیره‌شده ──
  const loadSelection = async (s) => {
    const j = await API(`/all-reports/saved/${s.id}/`);
    if (!j.ok) { say(j.msg); return; }
    setModes(j.modes || {});
    setOpts({});
    const ex = j.excluded || {};
    for (const k of Object.keys(ex)) await loadOpts(k, ex[k]);
    setResult(null);
  };
  const saveSelection = async () => {
    const excluded = {};
    Object.entries(opts).forEach(([k, rows]) => { const off = rows.filter(r => !r.sel).map(r => r.id); if (off.length) excluded[k] = off; });
    const body = JSON.stringify({ title: saveDlg.title, remark: saveDlg.remark, modes, excluded });
    const j = saveDlg.id
      ? await API(`/all-reports/saved/${saveDlg.id}/`, { method: 'PUT', body })
      : await API('/all-reports/saved/create/', { method: 'POST', body });
    if (!j.ok) { say(j.msg); return; }
    setSaveDlg(null); loadSaved();
  };
  const deleteSelection = s => setAsk({
    lines: ['آيا از حذف اين رکورد مطمئن هستيد؟'],
    onYes: async () => {
      setAsk(null);
      const j = await API(`/all-reports/saved/${s.id}/`, { method: 'DELETE' });
      if (j.ok) loadSaved(); else say(j.msg);
    },
  });

  if (fb.loaded && !fb.open) {
    return <ListPage user={user} onBack={onBack} title="گزارش کلی درخواست کالا / خدمات"><div style={ui.empty}>رکوردی یافت نشد</div></ListPage>;
  }

  const items = (init && init.items) || [];
  const list = opts[active] || [];
  const hasTitles = result && result.rows.some(r => r.RepTitle || r.RunningTotalTitle);
  const gridCols = result ? [
    ...(hasTitles ? [{ key: 'RepTitle', label: 'سرجمع گزارش', wrap: true }, { key: 'RunningTotalTitle', label: 'سرجمع میانی', wrap: true }] : []),
    ...result.cols.map(c => ({ key: c.key, label: c.label, num: c.num, wrap: !c.num })),
  ] : [];

  const toolbar = (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
      <button style={ui.btnNew} disabled={busy} onClick={prepare}>{busy ? '⏳' : 'تهیه گزارش'}</button>
      {result && <button style={ui.btnItems} onClick={print}>چاپ</button>}
      {result && <ExcelButton title="گزارش کلی درخواست کالا / خدمات" columns={gridCols} rows={result.rows} />}
      <button style={ui.btnEdit} onClick={() => setSaveDlg({ id: 0, title: '', remark: '' })}>ذخیره انتخاب‌ها</button>
    </div>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="گزارش کلی درخواست کالا / خدمات" toolbar={toolbar}>
        <div style={{ display: 'flex', height: '100%', gap: '8px' }}>
          {/* تنظیمات گزارش */}
          <div style={{ width: '380px', flexShrink: 0, overflow: 'auto', background: '#fff', borderLeft: '1px solid #e0e0e0', padding: '8px' }}>
            <div style={ui.row}>
              <DateField label="از تاریخ درخواست" value={dates.from_date} onChange={v => setDates(d => ({ ...d, from_date: v }))} />
              <DateField label="تا تاریخ" value={dates.to_date} onChange={v => setDates(d => ({ ...d, to_date: v }))} />
            </div>
            <div style={ui.row}>
              <DateField label="از تاریخ فاکتور" value={dates.from_factor_date} onChange={v => setDates(d => ({ ...d, from_factor_date: v }))} />
              <DateField label="تا تاریخ" value={dates.to_factor_date} onChange={v => setDates(d => ({ ...d, to_factor_date: v }))} />
            </div>
            <table style={{ ...ui.table, marginTop: '6px' }}>
              <tbody>
                {items.map(it => (
                  <tr key={it.name} onClick={() => setActive(it.name)}
                    style={{ background: active === it.name ? '#e8f0fe' : '#fff', cursor: 'pointer' }}>
                    <td style={{ ...ui.td, textAlign: 'right' }}>{it.label}</td>
                    <td style={ui.td}>
                      <select value={modes[it.name] || 0} onClick={e => e.stopPropagation()}
                        onChange={e => setModes(m => ({ ...m, [it.name]: Number(e.target.value) }))} style={{ ...ui.input, padding: '3px' }}>
                        {MODES.map((t, i) => <option key={i} value={i}>{t}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!NO_LIST.has(active) && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                  <button style={ui.btnCancel} onClick={() => setAll(active, 1)}>همه</button>
                  <button style={ui.btnCancel} onClick={() => setAll(active, 0)}>هیچ</button>
                  <button style={ui.btnCancel} onClick={() => setAll(active, 'inv')}>معکوس</button>
                </div>
                <div style={{ maxHeight: '260px', overflow: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                  {list.map(r => (
                    <label key={r.id} style={{ display: 'flex', gap: '6px', padding: '3px 6px', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!r.sel} onChange={() => toggle(active, r.id)} />
                      <span>{toFa(r.code)} {toFa(r.title)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div style={{ ...ui.flabel, marginTop: '10px' }}>انتخاب‌های ذخیره‌شده</div>
            {saved.map(s => (
              <div key={s.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', padding: '3px 0' }}>
                <span style={{ flex: 1, cursor: 'pointer', color: '#1a237e' }} title={s.remark} onClick={() => loadSelection(s)}>{s.title}</span>
                <button style={{ ...ui.btnCancel, padding: '2px 8px' }} onClick={() => setSaveDlg({ id: s.id, title: s.title, remark: s.remark })}>✎</button>
                <button style={{ ...ui.btnCancel, padding: '2px 8px', color: '#c62828' }} onClick={() => deleteSelection(s)}>✕</button>
              </div>
            ))}
          </div>
          {/* نتیجه */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            {result ? <Grid columns={gridCols} rows={result.rows} /> : <div style={ui.empty}>آیتم‌های گزارش را انتخاب کنید و «تهیه گزارش» را بزنید</div>}
          </div>
        </div>
      </ListPage>

      {saveDlg && (
        <Modal title="ذخیره انتخاب‌ها" width="520px" onClose={() => setSaveDlg(null)}
          footer={<><button style={ui.btnCancel} onClick={() => setSaveDlg(null)}>لغو</button>
            <button style={ui.btnSave} onClick={saveSelection}>ثبت</button></>}>
          <div style={ui.row}><Field label="عنوان" value={saveDlg.title} onChange={v => setSaveDlg(s => ({ ...s, title: v }))} /></div>
          <div style={ui.row}><Field label="توضیح" value={saveDlg.remark} onChange={v => setSaveDlg(s => ({ ...s, remark: v }))} /></div>
        </Modal>
      )}
      {ask && <AskBox lines={ask.lines} danger onYes={ask.onYes} onNo={() => setAsk(null)} />}
      {msg && <MsgBox lines={msg} onClose={() => setMsg(null)} />}
    </>
  );
}
